import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import { isPlainObject } from 'is-plain-object';
import React, { createContext, useMemo, useState } from 'react';
import type { SuggestedFilter } from '~/contracts/srp';
import type { DeepPartial } from '~/types';

type ContextType = {
  setSuggestedFiltersRequestId: React.Dispatch<React.SetStateAction<string>>;
  suggestedFilters: LoadableState<SuggestedFilter[]>;
  suggestedFiltersRequestId: string;
};

const defaultContext: ContextType = {
  setSuggestedFiltersRequestId() {},
  suggestedFilters: defaultLoadableState<SuggestedFilter[]>([]),
  suggestedFiltersRequestId: ''
};

const SuggestedFiltersContext = createContext<ContextType>(defaultContext);

export function SuggestedFiltersContextProvider({
  children
}: React.PropsWithChildren) {
  const suggestedFilters = useLoadableState<SuggestedFilter[]>([]);
  const [suggestedFiltersRequestId, setSuggestedFiltersRequestId] = useState(
    defaultContext.suggestedFiltersRequestId
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      setSuggestedFiltersRequestId,
      suggestedFilters,
      suggestedFiltersRequestId
    }),
    [suggestedFilters, suggestedFiltersRequestId]
  );

  return (
    <SuggestedFiltersContext.Provider value={value}>
      {children}
    </SuggestedFiltersContext.Provider>
  );
}

SuggestedFiltersContextProvider.displayName = 'SuggestedFiltersContextProvider';

export default SuggestedFiltersContext;

export const generateSuggestedFiltersContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(
    defaultContext,
    customState,
    { isMergeableObject: isPlainObject }
  );
